// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import { Box, Button, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import {
  sectionCharterSchoolEnrollPath,
  sectionEnrollPath
} from "@outschool/routes";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { Mutation, Query, gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  DayOfWeekSelector,
  LightActivityFragment,
  ScheduleSuggestionsQuery,
  useSearchFiltersString
} from "@outschool/ui-components-website";
import { Loading, Modal, Select } from "@outschool/ui-legacy-component-library";
import sortBy from "lodash/sortBy";
import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";

import { ACTIVITY_ENROLL_CLICK } from "../../../shared/Activity";
import { cleanTypeNameField } from "../../../shared/client/ApolloClient";
import * as Env from "../../../shared/Env";
import { isOrganizationAdminWhoCanEnroll } from "../../lib/Users";
import { SectionFragment } from "../../queries/SectionFragments";
import { useAppState } from "../../stores/AppStateProvider";
import ErrorMessage from "../ErrorMessage";

const START_MONTH_RANGE = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * @param noOfDays
 * @returns {number} - number of months rounded up. ei 45 days = 2 months
 */
const convertDaysToMonth = (noOfDays: $TSFixMe): number => {
  const today = dayjs();
  const end = dayjs().add(noOfDays, "days");
  const yearsApart = end.diff(today, "year");
  today.add(yearsApart, "years");

  const monthsApart = end.diff(today, "months");
  today.add(monthsApart, "months");

  const daysApart = end.diff(today, "days");

  if (daysApart > 0) {
    return yearsApart * 12 + monthsApart + 1;
  } else {
    return yearsApart * 12 + monthsApart;
  }
};

const START_END_DATE_FORMAT = "YYYY-MM-DD";

export function getStartAfterDateOptions(
  daysNotice: $TSFixMe,
  daysNoticeMax: $TSFixMe,
  timeZone: $TSFixMe
): {
  startAfter: dayjs.Dayjs;
  label: string;
  value: string;
}[] {
  const earliestStartDate = dayjs()
    .tz(timeZone)
    .add(daysNotice, "days")
    .startOf("day");

  const options = START_MONTH_RANGE.map(monthIndex => {
    if (monthIndex === 0) {
      const startAfter = dayjs(earliestStartDate);
      // Add startAfter value as first option.
      return {
        startAfter: startAfter,
        label: startAfter.format("[From] MMMM D, YYYY"),
        value: startAfter.format(START_END_DATE_FORMAT)
      };
    }

    const monthStart = dayjs(earliestStartDate)
      .startOf("month")
      .add(monthIndex, "months");

    return {
      startAfter: monthStart,
      label: monthStart.format("[From] MMMM YYYY"),
      value: monthStart.format(START_END_DATE_FORMAT)
    };
  });

  return options.slice(0, convertDaysToMonth(daysNoticeMax));
}

const MAX_RESULT_PER_PAGE = 5;

export default function ChooseATimeModal({ renderButton, activity }: $TSFixMe) {
  const [open, setOpen] = useState(false);
  const trackEvent = useTrackEvent();

  const onClick = () => {
    trackEvent("Choose a time clicked", { activityUid: activity.uid });
    setOpen(true);
  };
  const onClose = () => setOpen(false);

  return (
    <React.Fragment>
      {renderButton(onClick)}
      <Modal open={open} onClose={onClose} fullBleed={false}>
        <ModalContent activity={activity} closeModal={onClose} />
      </Modal>
    </React.Fragment>
  );
}

function ModalContent({ activity, closeModal }: $TSFixMe) {
  const SearchFiltersString = useSearchFiltersString();
  const { userTimeZone } = useSession();

  const [offset, setOffset] = useState(0);

  const startAfterDateOptions = useMemo(
    () =>
      getStartAfterDateOptions(
        activity.autoSchedulingDaysNoticeMin,
        activity.autoSchedulingDaysNoticeMax,
        userTimeZone
      ),
    [
      activity.autoSchedulingDaysNoticeMin,
      activity.autoSchedulingDaysNoticeMax,
      userTimeZone
    ]
  );

  const [startAfterDate, setStartAfterDate] = useState(
    startAfterDateOptions[0].value
  );

  const scheduleSuggestionsEndDate = dayjs()
    // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    .tz(userTimeZone)
    .add(activity.autoSchedulingDaysNoticeMax, "days");

  const onStartByChange = (startAfter: $TSFixMe) => {
    setStartAfterDate(startAfter);
    setOffset(0);
  };

  const [dow, setDow] = useState();
  const onDowChange = (dow: $TSFixMe) => {
    setDow(dow);
    setOffset(0);
  };

  const [startAfterTime, setStartAfterTime] = useState(
    Time.REASONABLE_DAY_START_HOUR
  ); // Default to 6am
  const [endByTime, setEndByTime] = useState(Time.REASONABLE_DAY_END_HOUR); // Default to 8pm

  const startAfterTimeOptions = useMemo(
    () => SearchFiltersString.startAfterTimeOptions(endByTime),
    [endByTime, SearchFiltersString]
  );
  const onStartAfterTimeChange = (startAfterTime: $TSFixMe) => {
    setStartAfterTime(startAfterTime);
    setOffset(0);
  };

  const endByTimeOptions = useMemo(
    () => SearchFiltersString.endByTimeOptions(startAfterTime),
    [startAfterTime, SearchFiltersString]
  );
  const onEndByTimeChange = (endByTime: $TSFixMe) => {
    setEndByTime(endByTime);
    setOffset(0);
  };

  const { t } = useTranslation(
    "client\\components\\detailPage\\ChooseATimeModal"
  );

  return (
    <>
      <Typography
        variant="h3"
        sx={{
          marginBottom: "1em"
        }}
      >
        {t`Choose a time`}
      </Typography>
      <Box
        flex
        sx={(theme: $TSFixMe) => ({
          flexDirection: "column",
          gap: "1em",

          [theme.breakpoints.up("md")]: {
            gap: "0.5em"
          },

          marginBottom: "1em"
        })}
      >
        <Box
          flex
          sx={(theme: $TSFixMe) => ({
            alignItems: "flexStart",

            [theme.breakpoints.up("md")]: {
              alignItems: "center",
              flexDirection: "row"
            },

            gap: "0.5em",
            flexDirection: "column"
          })}
        >
          <Select
            name="startAfter"
            options={startAfterDateOptions}
            value={startAfterDate}
            onChange={onStartByChange}
          />
          {/* @ts-ignore TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message */}
          <DayOfWeekSelector value={dow} onChange={onDowChange} />
        </Box>
        <Box
          flex
          sx={(theme: $TSFixMe) => ({
            alignItems: "flexStart",

            [theme.breakpoints.up("md")]: {
              alignItems: "center",
              flexDirection: "row"
            },

            gap: "0.5em",
            flexDirection: "column"
          })}
        >
          <Select
            name="startAfterTime"
            options={startAfterTimeOptions}
            value={startAfterTime}
            onChange={onStartAfterTimeChange}
          />
          <Select
            name="endByTime"
            options={endByTimeOptions}
            value={endByTime}
            onChange={onEndByTimeChange}
          />
        </Box>
      </Box>

      <ScheduleSuggestions
        activity={activity}
        startAfterDate={startAfterDate}
        endAtDate={scheduleSuggestionsEndDate}
        startAfterTime={startAfterTime}
        endByTime={endByTime}
        dow={dow}
        offset={offset}
        setOffset={setOffset}
        closeModal={closeModal}
      />
    </>
  );
}

function ScheduleSuggestions({
  activity,
  startAfterDate,
  endAtDate,
  startAfterTime,
  endByTime,
  dow,
  offset,
  setOffset,
  closeModal
}: $TSFixMe) {
  const { userTimeZone } = useSession();

  const startAfterDateISO = dayjs
    // @ts-ignore TS(2769): No overload matches this call.
    .tz(startAfterDate, userTimeZone)
    .toISOString();

  // @ts-ignore TS(2769): No overload matches this call.
  const endDateISO = dayjs.tz(endAtDate, userTimeZone).toISOString();

  return (
    <Query
      query={ScheduleSuggestionsQuery}
      variables={{
        uid: activity.uid,
        startAfter: startAfterDateISO,
        endAtDate: endDateISO,
        startAfterTime,
        endByTime,
        dow,
        timeZone: userTimeZone
      }}
    >
      {/* @ts-ignore TS(2322): Type '({ data, loading, error }: { data: any; load... Remove this comment to see the full error message */}
      {({ data, loading, error = null }) => {
        if (loading) {
          return <Loading />;
        }

        if (error) {
          return (
            <ErrorMessage
              value={error}
              showStatusPageLink={Env.IS_READ_ONLY_MODE}
            />
          );
        }

        const {
          activity: { scheduleSuggestions }
        } = data;

        if (scheduleSuggestions.length === 0) {
          return <>No availability</>;
        }

        const allScheduleSuggestions = sortBy(
          scheduleSuggestions,
          schedule => schedule[0].startTime
        );

        return (
          <>
            <Box
              flex
              sx={{
                flexDirection: "column",
                gap: "0.5em"
              }}
            >
              {allScheduleSuggestions
                .slice(offset, offset + MAX_RESULT_PER_PAGE)
                .map(suggestedMeetings => (
                  <SuggestedMeetings
                    key={suggestedMeetings[0].startTime}
                    activity={activity}
                    suggestedMeetings={suggestedMeetings}
                    closeModal={closeModal}
                  />
                ))}
            </Box>
            <ScheduleSuggestionsNavigation
              hasMoreResults={
                allScheduleSuggestions.length > offset + MAX_RESULT_PER_PAGE
              }
              offset={offset}
              setOffset={setOffset}
            />
          </>
        );
      }}
    </Query>
  );
}

export const acceptScheduleSuggestionMutation = gql`
  mutation AcceptScheduleSuggestion(
    $activityUid: ID!
    $suggestedMeetings: [MeetingSuggestionInput!]!
  ) {
    acceptScheduleSuggestion(
      activityUid: $activityUid
      suggestedMeetings: $suggestedMeetings
    ) {
      filledSpaceCount
      ...SectionFragment
      activity {
        ...LightActivityFragment
      }
    }
  }
  ${SectionFragment}
  ${LightActivityFragment}
`;

function SuggestedMeetings({
  activity,
  suggestedMeetings,
  closeModal
}: $TSFixMe) {
  const history = useHistory();
  const { currentUser, userTimeZone } = useSession();
  const trackEvent = useTrackEvent();

  const existingSection = suggestedMeetings[0].section;
  const appState = useAppState();
  const location = useLocation();
  const isOrganizationEnrollment = isOrganizationAdminWhoCanEnroll(currentUser);
  const buttonText = isOrganizationEnrollment ? "Admin Enroll" : "Enroll";

  const redirect = (
    section: $TSFixMe,
    activity: $TSFixMe,
    isAutoScheduled = false
  ) => {
    trackEvent(ACTIVITY_ENROLL_CLICK, {
      page: "chooseTimeModal",
      activity_uid: activity.uid,
      section_uid: section.uid,
      component: "SuggestedMeetings"
    });

    history.push(
      isOrganizationEnrollment
        ? sectionCharterSchoolEnrollPath(activity, section)
        : sectionEnrollPath(activity, section, isAutoScheduled)
    );
  };
  return (
    <Box
      flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        gap: "0.5em"
      }}
    >
      <div>
        {Time.fullDurationStringWithStartTime(
          suggestedMeetings[0].startTime,
          suggestedMeetings[suggestedMeetings.length - 1].endTime,
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          userTimeZone
        )}
      </div>
      {existingSection ? (
        <TrackedButton
          variant="contained"
          onClick={() => {
            redirect(existingSection, activity);
          }}
          trackingName="show_alternative_times_enroll"
          trackingUniqueId={activity?.uid}
          trackingSharedProperties={{
            existingSection: true
          }}
        >
          {buttonText}
        </TrackedButton>
      ) : (
        <Mutation
          mutation={acceptScheduleSuggestionMutation}
          variables={{
            activityUid: activity.uid,
            suggestedMeetings: suggestedMeetings.map((m: $TSFixMe) =>
              cleanTypeNameField(m)
            )
          }}
          onError={() => {
            // @ts-ignore TS(2769): No overload matches this call.
            history.push(appState.getEnrollPath(location));
          }}
          onCompleted={(data: $TSFixMe) => {
            const section = data.acceptScheduleSuggestion;
            redirect(section, section.activity, true);
            closeModal();
          }}
        >
          {(accept: $TSFixMe) => (
            <TrackedButton
              variant="contained"
              onClick={() => accept()}
              trackingName="show_alternative_times_enroll"
              trackingUniqueId={activity?.uid}
              trackingSharedProperties={{
                existingSection: false
              }}
            >
              {buttonText}
            </TrackedButton>
          )}
        </Mutation>
      )}
    </Box>
  );
}

function ScheduleSuggestionsNavigation({
  hasMoreResults,
  offset,
  setOffset
}: $TSFixMe) {
  const onNextResultPageClick = (e: $TSFixMe) => {
    e.preventDefault();
    setOffset((prev: $TSFixMe) => prev + MAX_RESULT_PER_PAGE);
  };
  const onPrevResultPageClick = (e: $TSFixMe) => {
    e.preventDefault();
    setOffset((prev: $TSFixMe) => prev - MAX_RESULT_PER_PAGE);
  };

  return (
    <Box
      flex
      sx={{
        justifyContent: "space-between",
        marginTop: "1em"
      }}
    >
      <Button
        variant="link"
        disabled={offset === 0}
        onClick={onPrevResultPageClick}
      >
        « previous
      </Button>
      <Button
        variant="link"
        disabled={!hasMoreResults}
        onClick={onNextResultPageClick}
      >
        next »
      </Button>
    </Box>
  );
}
